import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
  ViewChild,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { Hostel } from '@app/services/api/api.service';
import { LocaleService } from '@app/locale.service';

@Component({
  selector: 'app-hostel-description',
  templateUrl: './hostel-description.component.html',
  styleUrls: ['./hostel-description.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate(
          '0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class HostelDescriptionComponent
  implements AfterViewInit, AfterViewChecked, OnChanges
{
  @ViewChild('description', { static: false }) descriptionElement!: ElementRef;
  @Output() cardClicked = new EventEmitter<void>();
  @Input() public hostel?: Hostel;
  public showFullDescription: boolean = false;
  public isLongDescription: boolean = false;
  private destroyed = new Subject<void>();
  private checkedDescription: boolean = false; // Ensure we only check once

  constructor(
    private localeService: LocaleService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hostel'] && changes['hostel'].currentValue) {
      this.checkedDescription = false; // Reset flag when hostel changes
      this.cdr.detectChanges(); // Trigger change detection
    }
  }

  ngAfterViewInit() {
    console.log('ngAfterViewInit triggered');
  }

  ngAfterViewChecked() {
    if (this.descriptionElement && !this.checkedDescription && this.hostel) {
      // Run only once after the element is available
      this.checkedDescription = true;
      setTimeout(() => {
        this.checkDescriptionLength();
      }, 0); // Minor delay to ensure styles are applied
    }
  }

  public cardClick() {
    this.cardClicked.emit();
  }

  public getHostelDescription(): string {
    if (!this.hostel) {
      return '';
    }

    return this.hostel.descriptionTranslations
      ? this.hostel.descriptionTranslations[
          this.localeService.currentLocale.value
        ]
      : this.hostel.description;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public toggleDescription() {
    this.showFullDescription = !this.showFullDescription;
    this.cdr.markForCheck(); // Ensure Angular updates the view
  }

  private checkDescriptionLength() {
    const descriptionDiv = this.descriptionElement?.nativeElement;

    if (descriptionDiv) {
      if (descriptionDiv.innerText.trim().length === 0) {
        console.log('Description content is empty or not rendered');
        return;
      }

      const lineHeight =
        parseFloat(getComputedStyle(descriptionDiv).lineHeight) || 20;
      const scrollHeight = descriptionDiv.scrollHeight;

      if (lineHeight > 0 && scrollHeight > 0) {
        const numberOfLines = scrollHeight / lineHeight;

        this.isLongDescription = numberOfLines > 10;
        this.cdr.markForCheck(); // Trigger change detection
      } else {
        console.log(
          'Could not calculate number of lines correctly. Line height or scroll height is zero.'
        );
      }
    } else {
      console.log('Description element is not available');
    }
  }
}
